import { Box, Center } from '@mantine/core';
import { pageWrapper } from '@/Styles';
import { Client, Room, getStateCallbacks } from '@colyseus/sdk';
import { useEffect, useRef, useState, useMemo, Suspense, useCallback } from 'react';
import { useStore } from '../store/Store';
import { Canvas, ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import {
  Bvh,
  Environment,
  OrbitControls,
  useCursor,
  Box as THREEBox,
  Html,
  PivotControls,
  Gltf,
  Text,
} from '@react-three/drei';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';
import { getGameServerPath } from '../ApiPaths';

const map = { size: [16, 15], gridDivision: 2 };
const vector3ToGrid = (vector3: THREE.Vector3) => {
  return [Math.floor(vector3.x * map.gridDivision), Math.floor(vector3.z * map.gridDivision)];
};
const gridToVector3 = (gridPosition: number[], width = 1, height = 1) => {
  return new THREE.Vector3(
    width / map.gridDivision / 2 + gridPosition[0] / map.gridDivision,
    0,
    height / map.gridDivision / 2 + gridPosition[1] / map.gridDivision
  );
};
type Player = { x: number; z: number; name: string; message?: string; clientId: string };

function schemaPlayerToLocal(player: { x: number; z: number; name: string }, sessionId: string): Player {
  return {
    x: player.x,
    z: player.z,
    name: player.name,
    clientId: sessionId,
    message: '',
  };
}

const moveObjToPosition = (
  obj: THREE.Object3D,
  moveTarget: THREE.Vector3,
  deltaTimeMs: number,
  MOVEMENT_SPEED_m_s = 2
): boolean => {
  if (obj.position.distanceTo(moveTarget) < 0.1) {
    obj.position.set(moveTarget.x, 0, moveTarget.z);
    return true;
  }
  const direction = obj.position
    .clone()
    .sub(moveTarget)
    .normalize()
    .multiplyScalar(MOVEMENT_SPEED_m_s * deltaTimeMs);
  obj.position.sub(direction);
  return false;
};

function Avatar({ player }: { player: Player }) {
  const camera = useThree((state) => state.camera);
  const model = useRef<THREE.Group | null>(null);
  const nameRef = useRef<THREE.Mesh | null>(null);
  const chatRef = useRef<THREE.Mesh | null>(null);
  const position = useMemo(() => new THREE.Vector3(player.x, 0, player.z), []);
  const moveTarget = useMemo(() => new THREE.Vector3(player.x, 0, player.z), [player.x, player.z]);
  const [moveToTarget, setMoveToTarget] = useState(false);

  useEffect(() => {
    setMoveToTarget(true);
    model.current?.lookAt(moveTarget);
    const lookTarget = new THREE.Vector3(camera.position.x, 2, camera.position.z);
    nameRef.current?.lookAt(lookTarget);
    chatRef.current?.lookAt(lookTarget);
  }, [moveTarget, camera.position.x, camera.position.z]);

  useEffect(() => {
    const lookTarget = new THREE.Vector3(camera.position.x, 2, camera.position.z);
    nameRef.current?.lookAt(lookTarget);
    chatRef.current?.lookAt(lookTarget);
  }, [player.message, moveToTarget, camera.position.x, camera.position.z]);

  useFrame((_state, delta) => {
    if (!moveToTarget) return;
    const obj = model.current;
    if (obj && moveObjToPosition(obj, moveTarget, delta)) setMoveToTarget(false);
  });

  return (
    <group ref={model} position={position}>
      <Suspense fallback={null}>
        <Gltf src={'/Arbeiter.glb'} />
        <Text ref={nameRef} color="black" anchorX="center" anchorY={-2.4} fontSize={0.2}>
          {player.name}
        </Text>
        <Text ref={chatRef} color="black" anchorX="center" anchorY={-2.8} fontSize={0.26}>
          {player.message}
        </Text>
      </Suspense>
    </group>
  );
}

const DEFAULT_MACHINE = { x: 3, z: 13 };

export function DemoRoom() {
  const { t } = useTranslation();
  const user = useStore((state) => state.userProfile);
  const [room, setRoom] = useState<Room | undefined>(undefined);
  const [roomError, setRoomError] = useState<string | undefined>(undefined);
  const [ownPosition] = useState<THREE.Vector3>(
    new THREE.Vector3(map.size[0] / 2, 0, map.size[0] / 2)
  );

  const [chatMessages, setChatMessages] = useState<
    { clientId: string; message: string; name: string }[]
  >([
    {
      clientId: 'server',
      message: 'Herzlich Wilkommen in unseren begehbaren Multiplayer Demoraum!',
      name: 'service@robotop.de',
    },
    {
      clientId: 'server',
      message:
        'Zukünftig können Sie mit unserem ROBOPlaner eigene Showrooms, Onlineshops oder Projekte entwerfen und diese Online mit Kunden sowie Interessenten teilen.',
      name: 'service@robotop.de',
    },
  ]);

  const [machinePosition, setMachinePosition] = useState(DEFAULT_MACHINE);
  const [pivotMatrix, setPivotMatrix] = useState<THREE.Matrix4>(
    new THREE.Matrix4().compose(
      new THREE.Vector3(DEFAULT_MACHINE.x, 0, DEFAULT_MACHINE.z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, 0)),
      new THREE.Vector3(1, 1, 1)
    )
  );
  const [players, setPlayers] = useState<Map<string, Player>>(new Map());
  const [iFrameUri, setIFrameUri] = useState(
    'https://www.youtube.com/embed/4DOkpTdWYVU?si=GGxL5N8lT_T2LS43'
  );
  const [onFloor, setOnFloor] = useState(false);

  const machineRef = useRef<any>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLInputElement | null>(null);
  const playersBoundRef = useRef(false);
  const machineBoundRef = useRef(false);
  const iframeUriBoundRef = useRef(false);

  useCursor(onFloor);

  const upsertPlayer = useCallback(
    (player: { x: number; z: number; name: string }, sessionId: string) => {
      setPlayers((current) => {
        const next = new Map(current);
        const existing = next.get(sessionId);
        next.set(sessionId, {
          ...schemaPlayerToLocal(player, sessionId),
          message: existing?.message,
        });
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  useEffect(() => {
    const client = new Client(getGameServerPath(), {
      fetchFn: (url, init) => fetch(url, { ...init, credentials: 'omit' }),
    });
    let joinedRoom: Room | undefined;
    let active = true;
    playersBoundRef.current = false;
    machineBoundRef.current = false;
    iframeUriBoundRef.current = false;

    const bindStateListeners = (currentRoom: Room) => {
      const $ = getStateCallbacks(currentRoom);
      const state = currentRoom.state;
      if (!state) return;

      // Bind once — onStateChange fires on every patch; re-listen would leak.
      if (!iframeUriBoundRef.current) {
        iframeUriBoundRef.current = true;
        if (state.iframeUri) setIFrameUri(state.iframeUri);
        $(state).listen('iframeUri', (uri: string) => setIFrameUri(uri));
      }

      const syncMachine = () => {
        const machine = currentRoom.state.machine;
        if (!machine || machine.x == null || machine.z == null) return;
        setMachinePosition({ x: machine.x, z: machine.z });
        setPivotMatrix((current) =>
          current.clone().setPosition(new THREE.Vector3(machine.x, 0, machine.z))
        );
      };

      if (currentRoom.state.machine && !machineBoundRef.current) {
        machineBoundRef.current = true;
        syncMachine();
        $(currentRoom.state.machine).onChange(syncMachine);
      }

      // Schema v4: players is undefined until the first state patch — onAdd must attach after hydration.
      if (!state.players || playersBoundRef.current) return;
      playersBoundRef.current = true;

      $(state.players).onAdd((player, sessionId) => {
        upsertPlayer(player, sessionId);
        $(player).onChange(() => upsertPlayer(player, sessionId));
      }, true);

      $(state.players).onRemove((_player, sessionId) => {
        setPlayers((current) => {
          const next = new Map(current);
          next.delete(sessionId);
          return next;
        });
      });
    };

    client
      .joinOrCreate('DemoRoom')
      .then((newRoom) => {
        if (!active) {
          void newRoom.leave(true);
          return;
        }

        joinedRoom = newRoom;

        // Register before any server broadcasts (join/leave/chat).
        newRoom.onMessage('messages', (message: { clientId: string; message: string }) => {
          const player = newRoom.state?.players?.get(message.clientId);
          if (player) {
            setPlayers((current) => {
              const next = new Map(current);
              const existing = next.get(message.clientId);
              next.set(message.clientId, {
                ...schemaPlayerToLocal(player, message.clientId),
                message: message.message,
              });
              return next;
            });
          }
          setChatMessages((cm) => [
            ...cm,
            { ...message, name: player?.name || message.clientId },
          ]);
        });

        newRoom.onStateChange(() => bindStateListeners(newRoom));
        bindStateListeners(newRoom);

        setRoom(newRoom);
        setRoomError(undefined);
      })
      .catch((error: Error) => {
        console.error('Failed to join DemoRoom:', error);
        setRoomError(error.message || 'Verbindung zum Multiplayer-Server fehlgeschlagen');
      });

    return () => {
      active = false;
      playersBoundRef.current = false;
      machineBoundRef.current = false;
      iframeUriBoundRef.current = false;
      void joinedRoom?.leave(true);
      setRoom(undefined);
    };
  }, [upsertPlayer]);

  useEffect(() => {
    if (!room || !user?._id) return;
    const displayName = `[${user.companyName ?? ''}] ${user.username ?? ''}`.trim();
    if (displayName.length > 2) {
      room.send('setPlayerName', displayName);
    }
  }, [room, user?._id, user?.companyName, user?.username]);

  const sendMessage = (raw: string) => {
    const message = raw.trim();
    if (!message || !room) return;
    room.send('message', message);
  };

  const moveToClick = (e: ThreeEvent<MouseEvent>) => {
    if (!room) return;
    const coordinates = gridToVector3(vector3ToGrid(e.point));
    room.send('movePlayer', { x: coordinates.x, z: coordinates.z });
  };

  const moveMachine = () => {
    const machine = machineRef.current;
    if (machine && room) {
      room.send('moveMachine', { x: machine.position.x, z: machine.position.z });
    }
  };

  const setNewIFrameUri = () => {
    if (!room) return;
    room.send(
      'setIframeUri',
      iFrameUri === 'https://www.youtube.com/embed/4DOkpTdWYVU?si=GGxL5N8lT_T2LS43'
        ? 'https://robotop.de/'
        : 'https://www.youtube.com/embed/4DOkpTdWYVU?si=GGxL5N8lT_T2LS43'
    );
  };

  const machineX = machinePosition?.x ?? DEFAULT_MACHINE.x;
  const machineZ = machinePosition?.z ?? DEFAULT_MACHINE.z;

  return (
    <Center style={pageWrapper}>
      <Box
        style={{
          width: '80%',
          height: '94vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1>3D-Shop, Showroom</h1>
          {roomError && (
            <p style={{ color: 'crimson' }}>
              Multiplayer offline: {roomError}. Bitte platform-server (Port 3000) starten.
            </p>
          )}
          {!room && !roomError && <p>{t('demoRoom.connecting')}</p>}
          <Canvas
            style={{ width: '100%', height: '600px' }}
            shadows
            camera={{ position: [8, 18, 28], fov: 30 }}
          >
            <Bvh firstHitOnly>
              <Environment preset="sunset" />
              <ambientLight intensity={0.3} />
              <OrbitControls target={ownPosition} enableDamping={true} makeDefault />
              <color attach="background" args={['#ececec']} />

              <mesh
                rotation-x={-Math.PI / 2}
                position-y={-0.002}
                onClick={moveToClick}
                onPointerEnter={() => setOnFloor(true)}
                onPointerLeave={() => setOnFloor(false)}
                position-x={map.size[0] / 2}
                position-z={map.size[1] / 2}
              >
                <planeGeometry args={map.size as [width: number, height: number]} />
                <meshStandardMaterial color="#f0f0f0" />
              </mesh>

              <Html
                transform
                wrapperClass="htmlScreen"
                rotation={[0, 0.4, 0]}
                position={[3, 2, 5]}
                distanceFactor={2}
              >
                <iframe
                  height="400px"
                  width="800px"
                  src={iFrameUri}
                  allow="camera; xr-spatial-tracking; fullscreen"
                  allowFullScreen
                />
              </Html>
              <THREEBox
                onClick={(e) => {
                  e.stopPropagation();
                  setNewIFrameUri();
                }}
                args={[1, 2, 1]}
                position={[3, 1, 5]}
              >
                <meshStandardMaterial color="#3a9339" />
              </THREEBox>

              {Array.from(players.keys()).map((playerId) => {
                const player = players.get(playerId);
                if (player) return <Avatar key={playerId} player={player} />;
              })}

              <Suspense fallback={null}>
                <Gltf position={[8, 0, 9]} src={'/EcosphereExperienceCenter.glb'} />
                <Gltf
                  ref={machineRef}
                  position={[machineX, 0, machineZ]}
                  src={'/ecosphere_ecotending_static_low.glb'}
                />
                <PivotControls
                  depthTest={false}
                  onDrag={(local) => {
                    const machine = machineRef.current;
                    if (!machine) return;
                    const position = new THREE.Vector3();
                    const scale = new THREE.Vector3();
                    const quaternion = new THREE.Quaternion();
                    local.decompose(position, quaternion, scale);
                    machine.position.copy(position);
                    machine.rotation.setFromQuaternion(quaternion);
                    setPivotMatrix(local);
                  }}
                  onDragEnd={moveMachine}
                  matrix={pivotMatrix}
                />
              </Suspense>
            </Bvh>
          </Canvas>
        </div>

        <div ref={chatContainerRef} style={{ maxHeight: '150px', overflowY: 'auto' }}>
          {chatMessages.map((message, i) => (
            <p key={i}>{message.name + ': ' + message.message}</p>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!chatInputRef.current) return;
            sendMessage(chatInputRef.current.value);
            chatInputRef.current.value = '';
          }}
        >
          <input
            ref={chatInputRef}
            type="text"
            disabled={!room}
            placeholder={room ? 'Nachricht…' : 'Warte auf Verbindung…'}
            autoFocus
          />
          <input type="submit" value="Nachricht senden" disabled={!room} />
        </form>
      </Box>
    </Center>
  );
}
